import { Component } from '@theme/component';

const setText = (node,value) => { if (node.textContent !== value) node.textContent = value; };
const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
const styleTags = new Set(['hilo','7 nudos','cadena','piedra natural','perlas','circonitas','minimalista','geometrico','martillado','trenzado','cuentas','brazalete','aro','flor','corazon']);
const optionNames = new Set(['color','colour','acabado','color del hilo','hilo']);
const shared = (a = [], b = []) => new Set(a.filter(value => b.includes(value))).size;
const colors = values => (values || []).map(normalize).filter(value => /^(dorado|plateado|oro rosa|rojo|negro|blanco|azul|verde|rosa|morado|gold|silver|rose gold|red|black|white|blue|green|pink)$/.test(value));
const productColors = (title, options) => {
  const explicit = colors(options);
  if (explicit.length) return explicit;
  return [...normalize(title).matchAll(/\b(platead[oa]|dorad[oa]|oro rosa)\b/g)].map(match => match[1].replace('plateada','plateado').replace('dorada','dorado'));
};

export function matchCandidate(anchors, candidates, cart, threshold) {
  if (cart.currency !== 'EUR' || !cart.requires_shipping || !Number.isSafeInteger(cart.total_price) || cart.total_price < 0 || !Number.isSafeInteger(threshold)) return null;
  const gap = threshold - cart.total_price;
  if (gap <= 0 || !cart.item_count) return null;
  const cartIds = new Set(cart.items.map(item => String(item.product_id)));
  const current = anchors.filter(anchor => cartIds.has(String(anchor.id)));
  const ranked = [];
  for (const candidate of candidates) {
    const p = candidate.product;
    if (!p || cartIds.has(String(p.id)) || (p.options || []).some(name => !optionNames.has(normalize(name)) && normalize(name) !== 'title')) continue;
    for (const variant of candidate.variants || []) {
      if (!Number.isSafeInteger(variant.price) || variant.price < gap || variant.price > gap + 1000 || !variant.image) continue;
      let best = 0;
      for (const anchor of current) {
        const symbolCount = shared([...(p.symbols || []), ...(variant.symbols || [])], anchor.symbols || []);
        const seriesCount = shared(p.series || [], anchor.series || []);
        const styles = (p.tags || []).map(normalize).filter(tag => styleTags.has(tag));
        const anchorStyles = (anchor.tags || []).map(normalize).filter(tag => styleTags.has(tag));
        const styleCount = shared(styles, anchorStyles);
        const anchorColors = productColors(anchor.title,anchor.variantOptions), variantColors = productColors(p.title,variant.options);
        const metal = value => ['dorado','plateado','oro rosa','gold','silver','rose gold'].includes(value);
        const metals = anchorColors.filter(metal), candidateMetals = variantColors.filter(metal);
        if (metals.length && candidateMetals.length && !shared(metals, candidateMetals)) continue;
        const colorCount = shared(anchorColors, variantColors);
        const sameType = normalize(p.type) === normalize(anchor.type);
        if (!symbolCount && !seriesCount && !(sameType && styleCount >= 2 && colorCount)) continue;
        best = Math.max(best, Math.min(seriesCount, 2) * 30 + Math.min(symbolCount, 3) * 40 + Math.min(styleCount, 2) * 5 + Math.min(colorCount, 2) * 8 + (sameType ? 2 : 0));
      }
      if (best) ranked.push({product:p, variant, url:candidate.url, score:best, overage:variant.price-gap});
    }
  }
  ranked.sort((a,b) => b.score-a.score || a.overage-b.overage || String(a.variant.id).localeCompare(String(b.variant.id)));
  return ranked[0] || null;
}

class CotigoCartRecommendations extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (this.listenerController) return;
    this.listenerController = new AbortController();
    this.sequence = 0;
    const options = {signal:this.listenerController.signal};
    for (const name of ['shopify:cart:lines-update','shopify:cart:discount-update','shopify:cart:view','cart-section:restored']) document.addEventListener(name, this.onCart, options);
    window.addEventListener('pageshow', this.onPageShow, options);
    this.contentObserver = new MutationObserver(() => {
      this.loadConfig();
      if (this.isConnected && this.config) this.render();
    });
    this.contentObserver.observe(this,{childList:true,subtree:true,characterData:true});
    this.loadConfig();
  }

  updatedCallback() {
    super.updatedCallback();
    this.loadConfig();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.sequence++;
    this.contentObserver?.disconnect();
    this.listenerController?.abort();
    this.request?.abort();
    this.listenerController = null;
    this.signature = null;
  }

  loadConfig() {
    const text = this.querySelector('[data-cart-recommendations]')?.textContent;
    if (!text || text === this.signature) return;
    try {
      const next = JSON.parse(text);
      this.config = next;
      this.signature = text;
      this.cart = next.cart;
      this.render();
    } catch {
      this.config = null;
      this.hidden = true;
    }
  }

  onPageShow = () => this.refresh();

  onCart = async event => {
    this.sequence++;
    this.request?.abort();
    const row = this.querySelector('[data-recommendation]');
    if (row) row.hidden = true;
    try { await event.promise; } catch {}
    if (this.isConnected) this.refresh();
  };

  async refresh() {
    if (!this.config) return;
    const sequence = ++this.sequence;
    this.request?.abort();
    this.request = new AbortController();
    try {
      const response = await fetch(this.config.cartUrl, {cache:'no-store',credentials:'same-origin',signal:this.request.signal});
      if (!response.ok) throw new Error('cart');
      const cart = await response.json();
      if (sequence !== this.sequence || !this.isConnected) return;
      this.loadConfig();
      this.cart = cart;
      this.render();
    } catch (error) {
      if (sequence !== this.sequence || error.name === 'AbortError' || !this.isConnected) return;
      this.querySelector('[data-message]').textContent = this.config.text.unavailable;
      this.querySelector('[data-recommendation]').hidden = true;
      this.querySelector('[data-progress]').style.transform = 'scaleX(0)';
      this.querySelector('[role=progressbar]').setAttribute('aria-valuenow','0');
    }
  }

  render() {
    if (!this.config || !this.cart) return;
    const cart = this.cart;
    const header = document.querySelector('cotigo-shipping-bar:not(.csb-cart) [data-config]');
    let threshold = this.config.threshold;
    try { if (header) threshold = JSON.parse(header.textContent).threshold; } catch {}
    const valid = cart.currency === 'EUR' && Number.isSafeInteger(cart.total_price) && cart.total_price >= 0 && Number.isSafeInteger(threshold) && threshold > 0;
    this.hidden = !cart.item_count;
    const text = this.config.text;
    const money = value => new Intl.NumberFormat(document.documentElement.lang || 'es',{style:'currency',currency:cart.currency || 'EUR'}).format(value/100);
    const state = !valid ? 'unavailable' : !cart.requires_shipping ? 'digital' : cart.total_price >= threshold ? 'reached' : 'remaining';
    setText(this.querySelector('[data-message]'),text[state].replace('[amount]',money(Math.max(0,threshold-cart.total_price))));
    const ratio = valid && cart.requires_shipping ? Math.min(1,cart.total_price/threshold) : 0;
    this.querySelector('[data-progress]').style.transform = `scaleX(${ratio})`;
    const track = this.querySelector('[role=progressbar]');
    track.setAttribute('aria-valuemax',String(threshold));
    track.setAttribute('aria-valuenow',String(valid ? Math.min(cart.total_price,threshold) : 0));
    const row = this.querySelector('[data-recommendation]');
    this.recommendation = valid ? matchCandidate(this.config.anchors,this.config.candidates,cart,threshold) : null;
    row.hidden = !this.recommendation;
    if (!this.recommendation) return;
    const {product,variant,url} = this.recommendation;
    const link = new URL(url,location.origin);
    if (link.origin !== location.origin) {row.hidden=true;return;}
    link.searchParams.set('variant',String(variant.id));
    const title = this.querySelector('[data-title]');
    setText(title,product.title);
    title.href = link.href;
    title.title = product.title;
    this.querySelector('[data-image-link]').href = link.href;
    const image = this.querySelector('[data-image]');
    image.src = variant.image;
    image.alt = product.title;
    setText(this.querySelector('[data-price]'),money(variant.price));
  }

  async addRecommendation() {
    if (this.adding || !this.recommendation) return;
    const button = this.querySelector('[data-add]'), error = this.querySelector('[data-error]');
    const variantId = this.recommendation.variant.id;
    this.adding = true;
    button.disabled = true;
    button.textContent = this.config.text.adding;
    error.hidden = true;
    try {
      if (!window.Shopify?.actions?.updateCart) throw new Error(this.config.text.error);
      const result = await window.Shopify.actions.updateCart({lines:[{merchandiseId:String(variantId),quantity:1}]},{event:{context:'cart'}});
      if (result.userErrors?.length || result.detail?.didError) throw new Error(result.userErrors?.[0]?.message || this.config.text.error);
      if (this.isConnected) await this.refresh();
    } catch (failure) {
      if (this.isConnected) {
        error.textContent = failure.message || this.config.text.error;
        error.hidden = false;
      }
    } finally {
      this.adding = false;
      if (this.isConnected) {
        button.disabled = false;
        button.textContent = this.config.text.add;
        if (this.querySelector('[data-recommendation]').hidden) this.closest('cart-items-component')?.querySelector('[name=checkout]')?.focus();
      }
    }
  }
}

if (!customElements.get('cotigo-cart-recommendations')) customElements.define('cotigo-cart-recommendations',CotigoCartRecommendations);
